import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Select } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const VERSIONS = ["v4", "v7"];
const MIN_COUNT = 1;
const MAX_COUNT = 100;

const getCrypto = () => {
  if (typeof window !== "undefined" && window.crypto) return window.crypto;
  return null;
};

const randomBytes = (length) => {
  const cryptoObj = getCrypto();
  const bytes = new Uint8Array(length);
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return bytes;
};

const bytesToUuid = (bytes) => {
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20, 32)}`
  );
};

const generateV4 = () => {
  const cryptoObj = getCrypto();
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  const bytes = randomBytes(16);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return bytesToUuid(bytes);
};

const generateV7 = () => {
  const bytes = randomBytes(16);
  const ms = BigInt(Date.now());
  bytes[0] = Number((ms >> 40n) & 0xffn);
  bytes[1] = Number((ms >> 32n) & 0xffn);
  bytes[2] = Number((ms >> 24n) & 0xffn);
  bytes[3] = Number((ms >> 16n) & 0xffn);
  bytes[4] = Number((ms >> 8n) & 0xffn);
  bytes[5] = Number(ms & 0xffn);
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return bytesToUuid(bytes);
};

const generateOne = (version) => (version === "v7" ? generateV7() : generateV4());

const clampCount = (value) => {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) return MIN_COUNT;
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, n));
};

const applyFormat = (uuid, { uppercase, hyphens, braces }) => {
  let out = uuid;
  if (!hyphens) out = out.replace(/-/g, "");
  if (uppercase) out = out.toUpperCase();
  if (braces) out = `{${out}}`;
  return out;
};

const UUIDGenerator = () => {
  const { t } = useTranslation();
  const [version, setVersion] = useState("v4");
  const [count, setCount] = useState(5);
  const [uppercase, setUppercase] = useState(false);
  const [hyphens, setHyphens] = useState(true);
  const [braces, setBraces] = useState(false);
  const [uuids, setUuids] = useState(() =>
    Array.from({ length: 5 }, () => generateV4())
  );

  const generate = useCallback(() => {
    const next = Array.from({ length: clampCount(count) }, () => generateOne(version));
    setUuids(next);
  }, [count, version]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      e.preventDefault();
      generate();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [generate]);

  const formatOpts = useMemo(
    () => ({ uppercase, hyphens, braces }),
    [uppercase, hyphens, braces],
  );

  const formatted = useMemo(
    () => uuids.map((u) => applyFormat(u, formatOpts)),
    [uuids, formatOpts],
  );

  const joined = useMemo(() => formatted.join("\n"), [formatted]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">{t("uuid_gen_title")}</h1>
            <p className="text-gray-600">{t("uuid_gen_intro")}</p>

            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("uuid_gen_version_label")}
                </label>
                <Select
                  value={version}
                  onChange={setVersion}
                  style={{ width: 160 }}
                  options={VERSIONS.map((v) => ({
                    value: v,
                    label: v === "v7" ? t("uuid_gen_v7_option") : t("uuid_gen_v4_option"),
                  }))}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("uuid_gen_count_label")}
                </label>
                <input
                  type="number"
                  min={MIN_COUNT}
                  max={MAX_COUNT}
                  value={count}
                  onChange={(e) => setCount(clampCount(e.target.value))}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <StandardButton
                onClick={generate}
                type="primary"
                size="middle"
                aria-keyshortcuts="Meta+Enter Control+Enter"
              >
                {t("uuid_gen_generate")}
              </StandardButton>
              <StandardButton
                onClick={() => copy(joined, t("uuid_gen_copy_all_label"))}
                type="secondary"
                variant="outlined"
                size="middle"
              >
                {t("uuid_gen_copy_all")}
              </StandardButton>
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              {t("uuid_gen_shortcut_hint")}
            </p>

            <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-2 border border-gray-200 rounded-md p-3">
              <legend className="px-1 text-xs font-medium text-gray-600">
                {t("uuid_gen_format_legend")}
              </legend>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={uppercase}
                  onChange={(e) => setUppercase(e.target.checked)}
                  className="h-4 w-4"
                />
                {t("uuid_gen_format_uppercase")}
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={hyphens}
                  onChange={(e) => setHyphens(e.target.checked)}
                  className="h-4 w-4"
                />
                {t("uuid_gen_format_hyphens")}
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={braces}
                  onChange={(e) => setBraces(e.target.checked)}
                  className="h-4 w-4"
                />
                {t("uuid_gen_format_braces")}
              </label>
            </fieldset>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
              <ul className="flex flex-col divide-y divide-indigo-100">
                {formatted.map((u, idx) => (
                  <li
                    key={`${u}-${idx}`}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <span className="font-mono text-sm break-all text-gray-800">{u}</span>
                    <StandardButton
                      onClick={() => copy(u, t("uuid_gen_uuid_label"))}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("copy_button")}
                    </StandardButton>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("uuid_gen_note_v4")}</p>
              <p>{t("uuid_gen_note_v7")}</p>
              <p>{t("uuid_gen_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default UUIDGenerator;
